import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { authColors, shadows } from '../../styles/colors';
import { setPasswordRecoveryPending, clearPasswordRecoveryPending } from '../../utils/passwordRecoveryStorage';
import OtpVerification from './OtpVerification';

interface ForgotPasswordFormProps {
  onBackToLogin: () => void;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ onBackToLogin }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const { sendOtp } = useAuth();
  const { t } = useI18n();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Send OTP for password recovery
    const { error: otpError } = await sendOtp(email.trim(), undefined, 'recovery');
    
    if (otpError) {
      // Surface rate limit message: with seconds if available, else generic
      const rateLimited = (otpError as any)?.isRateLimited;
      const waitSeconds = (otpError as any)?.waitSeconds;
      if (rateLimited) {
        if (typeof waitSeconds === 'number') {
          setError(t('auth.otpResendRateLimited', { seconds: waitSeconds }));
        } else {
          setError(t('auth.otpRateLimitedGeneric'));
        }
      } else {
        setError(t('auth.otpResendError'));
      }
      // Critical: do not advance to OTP screen on failure
      setShowOtp(false);
      setLoading(false);
      return;
    } else {
      setPasswordRecoveryPending(email.trim());
      setShowOtp(true);
      setError('');
      setLoading(false);
    }
  };

  const handleBackFromOtp = () => {
    clearPasswordRecoveryPending();
    setShowOtp(false);
    setError('');
  };

  if (showOtp) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8">
        <OtpVerification
          email={email.trim()}
          onBack={handleBackFromOtp}
          type="recovery"
          context="forgot"
        />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8">
      <div className="text-center mb-8">
        <div className={`mx-auto h-12 w-12 ${authColors.iconGradient} rounded-full flex items-center justify-center mb-4`}>
          <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          {t('auth.forgotPasswordTitle')}
        </h2>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            {t('auth.emailAddress')}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
            placeholder={t('auth.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-red-700 text-sm text-center">{error}</p>
          </div>
        )}

        <div className="space-y-3">
          <button
            type="submit"
            disabled={loading}
            className={`w-full ${authColors.primaryButton} ${shadows.medium} focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? t('auth.sending') : t('auth.sendResetCode')}
          </button>

          <button
            type="button"
            onClick={onBackToLogin}
            className={`w-full ${authColors.authLink} py-2 px-4 rounded-lg hover:bg-blue-50 transition-colors`}
          >
            {t('auth.backToLogin')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ForgotPasswordForm;
